import React, { useState, useEffect } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import FormActionButtons from "../Assets/FormActionButtons";
import LanguageTabs, { FormLang } from "../Assets/LanguageTabs";
import { useTranslation } from "react-i18next";

interface AboutUsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (formData: FormData) => void;
}

type Multi = { en: string; mk: string; sq: string };
const emptyMulti: Multi = { en: "", mk: "", sq: "" };

const AboutUsModal: React.FC<AboutUsModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [activeLang, setActiveLang] = useState<FormLang>("en");
  const [name, setName] = useState<Multi>({ ...emptyMulti });
  const [text, setText] = useState<Multi>({ ...emptyMulti });
  const [subcategory, setSubcategory] = useState<Multi>({ ...emptyMulti });
  const [images, setImages] = useState<File[]>([]);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    const newErrors: { [key: string]: string } = {};
    (["en", "mk", "sq"] as FormLang[]).forEach((l) => {
      if (name[l] && subcategory[l]) {
        newErrors[`subcategory_${l}`] = "Subcategory must be empty when name is filled.";
      }
    });
    if (!text.en || !text.mk) {
      newErrors.text = "Text is required in EN and MK.";
    }
    setErrors(newErrors);
  }, [name, subcategory, text]);

  const setField = (
    setter: React.Dispatch<React.SetStateAction<Multi>>,
    value: string
  ) => setter((prev) => ({ ...prev, [activeLang]: value }));

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newImages = Array.from(e.target.files);
      setImages((prev) => [...prev, ...newImages]);
    }
  };

  const handleRemoveImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (Object.keys(errors).length > 0) return;

    const formData = new FormData();
    (["en", "mk", "sq"] as FormLang[]).forEach((l) => {
      formData.append(`name[${l}]`, name[l]);
      formData.append(`subcategory[${l}]`, subcategory[l]);
      formData.append(`text[${l}]`, text[l]);
    });
    images.forEach((img) => formData.append("images", img));

    onSubmit(formData);
    resetForm();
    onClose();
  };

  const resetForm = () => {
    setName({ ...emptyMulti });
    setText({ ...emptyMulti });
    setSubcategory({ ...emptyMulti });
    setImages([]);
    setErrors({});
    setActiveLang("en");
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link"],
      ["clean"],
    ],
  };

  const quillFormats = [
    "header", "bold", "italic", "underline", "strike",
    "list", "bullet", "link"
  ];

  if (!isOpen) return null;

  const langSuffix = activeLang.toUpperCase();

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
      <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-3xl relative max-h-[90vh] overflow-y-auto font-montserrat">
        <h2 className="text-2xl font-semibold mb-6 text-brand-800 text-center">{t(`forms.createAbout`)}</h2>

        <LanguageTabs active={activeLang} onChange={setActiveLang} />

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block font-medium mb-1">{t('forms.name')} ({langSuffix})</label>
            <input
              type="text"
              value={name[activeLang]}
              onChange={(e) => setField(setName, e.target.value)}
              className={`w-full border border-gray-300 p-2 rounded focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-brand-400 transition-colors ${errors[`subcategory_${activeLang}`] ? 'border-red-500' : ''}`}
            />
          </div>

          <div>
            <label className="block font-medium mb-1">{t('forms.subcategory')} ({langSuffix})</label>
            <input
              type="text"
              value={subcategory[activeLang]}
              onChange={(e) => setField(setSubcategory, e.target.value)}
              className={`w-full border border-gray-300 p-2 rounded focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-brand-400 transition-colors ${errors[`subcategory_${activeLang}`] ? 'border-red-500' : ''}`}
            />
            {errors[`subcategory_${activeLang}`] && (
              <span className="text-red-500 text-sm">{errors[`subcategory_${activeLang}`]}</span>
            )}
          </div>

          <div>
            <label className="block font-medium mb-1">{t('forms.text')} ({langSuffix})</label>
            <ReactQuill
              key={`text-${activeLang}`}
              value={text[activeLang]}
              onChange={(value) => setField(setText, value)}
              theme="snow"
              className="h-48 mb-4"
              placeholder="..."
              modules={quillModules}
              formats={quillFormats}
            />
          </div>

          <br />
          <br />
          <div className="space-y-4">
            <label className="block font-medium text-lg">{t(`forms.images`)}</label>
            <div className="flex flex-wrap gap-4">
              {images.map((image, index) => (
                <div key={index} className="relative w-24 h-24">
                  <img
                    src={URL.createObjectURL(image)}
                    alt={`Preview ${index}`}
                    className="w-full h-full object-cover rounded-md"
                  />
                  <button
                    type="button"
                    className="absolute top-0 right-0 text-red-500 hover:text-red-700"
                    onClick={() => handleRemoveImage(index)}
                  >
                    ✕
                  </button>
                </div>
              ))}
              <label
                htmlFor="imageUpload"
                className="cursor-pointer border-2 border-dashed border-gray-400 p-4 rounded-md w-24 h-24 flex justify-center items-center text-gray-600 hover:bg-gray-100"
              >
                <span className="text-2xl">+</span>
                <input
                  id="imageUpload"
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
              </label>
            </div>
          </div>

          <FormActionButtons
            edit={false}
            onClose={handleCancel}
            onSubmit={handleSubmit}
            disabled={!text.en || !text.mk}
          />
        </form>
      </div>
    </div>
  );
};

export default AboutUsModal;
